import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  findPatientByPatientId,
  initialPatients,
  type PatientRecord,
} from '../data/patientData'
import { listEligibilityChecks } from '../services/eligibilityChecksService'
import { mapEligibilityCheckToPatientRecord } from '../services/mapEligibilityCheckToPatientRecord'

type PatientsContextValue = {
  patients: PatientRecord[]
  isLoadingPatients: boolean
  patientsLoadError: string | null
  getPatientByEligibilityCheckId: (eligibilityCheckId: string) => PatientRecord | undefined
  getPatientByPatientId: (patientId: string) => PatientRecord | undefined
  updatePatient: (id: string, patch: Partial<PatientRecord>) => void
  setPatients: (records: PatientRecord[]) => void
}

const PatientsContext = createContext<PatientsContextValue | null>(null)

export function PatientsProvider({ children }: { children: ReactNode }) {
  const [patients, setPatients] = useState<PatientRecord[]>(initialPatients)
  const [isLoadingPatients, setIsLoadingPatients] = useState<boolean>(true)
  const [patientsLoadError, setPatientsLoadError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true

    async function hydratePatientsFromApi() {
      try {
        setIsLoadingPatients(true)
        setPatientsLoadError(null)
        const checks = await listEligibilityChecks()
        const mapped = checks
          .map((check, index) => mapEligibilityCheckToPatientRecord(check, index))
          .filter((record): record is PatientRecord => record !== null)
        if (isMounted && mapped.length > 0) {
          setPatients(mapped)
        }
      } catch (error) {
        if (isMounted) {
          setPatientsLoadError(
            error instanceof Error
              ? error.message
              : 'Failed to load patients from eligibility checks API.'
          )
        }
      } finally {
        if (isMounted) {
          setIsLoadingPatients(false)
        }
      }
    }

    void hydratePatientsFromApi()

    return () => {
      isMounted = false
    }
  }, [])

  const getPatientByEligibilityCheckId = useCallback(
    (eligibilityCheckId: string) => patients.find((record) => record.id === eligibilityCheckId),
    [patients]
  )

  const getPatientByPatientId = useCallback(
    (patientId: string) => findPatientByPatientId(patients, patientId),
    [patients]
  )

  const updatePatient = useCallback(
    (id: string, patch: Partial<PatientRecord>) => {
      setPatients((prev) =>
        prev.map((record) => (record.id === id ? { ...record, ...patch } : record))
      )
    },
    []
  )

  const value = useMemo<PatientsContextValue>(
    () => ({
      patients,
      isLoadingPatients,
      patientsLoadError,
      getPatientByEligibilityCheckId,
      getPatientByPatientId,
      updatePatient,
      setPatients,
    }),
    [
      patients,
      isLoadingPatients,
      patientsLoadError,
      getPatientByEligibilityCheckId,
      getPatientByPatientId,
      updatePatient,
    ]
  )

  return <PatientsContext.Provider value={value}>{children}</PatientsContext.Provider>
}

export function usePatients(): PatientsContextValue {
  const ctx = useContext(PatientsContext)
  if (!ctx) {
    throw new Error('usePatients must be used inside a <PatientsProvider>')
  }
  return ctx
}
